import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Plus, Search } from "lucide-react";
import styles from "@/components/sheet/LembarVisual.module.css";
import { Button } from "@/components/ax/Button";
import { EmptyAxis } from "@/components/ax/EmptyAxis";
import { PrimaryBar } from "@/components/ax/PrimaryBar";
import { Chip, KIND_KEY } from "@/components/sheet/KindChips";
import { useFirstView } from "@/hooks/useFirstView";
import { useI18n } from "@/i18n";
import { listSheets, sheetTitle, snippet } from "@/lib/sheetStore";

const FILTERS = ["all", "tujuan", "sasaran", "objektif", "jurnal", "bebas", "archived"];

const Skeleton = () => (
  <div aria-busy="true" data-testid="sheet-skeleton" className={styles.cards}>
    {[0, 1, 2].map((i) => (
      <div key={i} className={styles.skeleton}>
        <div className="h-4 w-2/3 rounded-[8px] bg-ax-border" />
        <div className="mt-3 h-3.5 w-full rounded-[8px] bg-ax-border" />
        <div className="mt-2 h-3.5 w-1/3 rounded-[8px] bg-ax-border" />
      </div>
    ))}
  </div>
);

export default function Lembar() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const first = useFirstView("sheets");
  const [state, setState] = useState("loading");
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");

  const load = useCallback(async () => {
    setState("loading");
    const res = await listSheets();
    if (res.error) return setState("error");
    setList(res.sheets);
    setState("ready");
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim().toLowerCase()), 200);
    return () => clearTimeout(id);
  }, [query]);

  const visible = useMemo(
    () =>
      list.filter((s) => {
        const inFilter =
          filter === "archived"
            ? s.status === "archived"
            : s.status !== "archived" && (filter === "all" || s.kind === filter);
        if (!inFilter) return false;
        if (!debounced) return true;
        return `${sheetTitle(s)} ${snippet(s)}`.toLowerCase().includes(debounced);
      }),
    [list, filter, debounced],
  );

  const fmt = new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "id-ID", { day: "numeric", month: "short", year: "numeric" });

  return (
    <div data-testid="sheet-list-page" className={styles.page}>
      <p className={styles.eyebrow}>{t("sheet.listEyebrow")}</p>
      <div className={styles.heading}>
      <h1 className={styles.title} data-testid="sheet-list-title">
        {t("sheet.title")}
      </h1>
      {state === "ready" && <span className={styles.count}>{t("sheet.listCount", { count: list.length })}</span>}
      </div>
      {!(state === "ready" && list.length === 0) && (
        <p className={styles.lede}>{t("sheet.listLede")}</p>
      )}

      {state === "loading" && <Skeleton />}

      {state === "error" && (
        <div role="alert" data-testid="sheet-list-error" className={`${first ? "enter " : ""}${styles.error}`}>
          <p className="m-0 text-base text-ax-text2">{t("sheet.failLoad")}</p>
          <Button onClick={load} data-testid="sheet-retry-button" className="mt-4">
            {t("retry")}
          </Button>
        </div>
      )}

      {state === "ready" && list.length === 0 && (
        <section data-testid="sheet-empty" className={`${first ? "enter " : ""}${styles.empty}`}>
          <EmptyAxis play={first} />
          <p className={styles.emptyText}>{t("sheet.empty")}</p>
          <p className={styles.emptyHint}>{t("sheet.listEmptyHint")}</p>
        </section>
      )}

      {state === "ready" && list.length > 0 && (
        <div className={first ? "enter" : ""}>
          <div className={styles.toolbar}>
          <label htmlFor="sheet-search" className="sr-only">
            {t("sheet.searchPh")}
          </label>
          <div className={styles.search}><Search size={18} strokeWidth={1.5} aria-hidden="true" /><input
            id="sheet-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("sheet.searchPh")}
            data-testid="sheet-search-input"
            className="h-12 w-full rounded-[8px] border border-ax-border bg-ax-surface px-3 text-base text-ax-text placeholder:text-ax-muted focus:border-ax-accent focus:outline-none"
          /></div>
          <div
            role="group"
            aria-label={t("sheet.filters")}
            className={styles.filters}
          >
            {FILTERS.map((f) => (
              <Chip
                key={f}
                active={filter === f}
                onClick={() => setFilter(f)}
                testId={`sheet-filter-${f}`}
                label={f === "all" ? t("filter.all") : f === "archived" ? t("sheet.status.archived") : t(KIND_KEY[f])}
              />
            ))}
          </div>
          </div>

          {visible.length === 0 ? (
            <p
              data-testid={debounced ? "sheet-search-empty" : "sheet-filter-empty"}
              className="m-0 mt-8 text-base text-ax-text"
            >
              {t(debounced ? "sheet.searchEmpty" : "sheet.filterEmpty")}
            </p>
          ) : (
            <ul className={styles.cards}>
              {visible.map((s) => (
                <li key={s.id}>
                  <Link
                    to={`/lembar/${s.id}`}
                    data-testid={`sheet-row-${s.id}`}
                    className={styles.card}
                  >
                    <span className={styles.metadata}><span className={styles.kind}>{t(KIND_KEY[s.kind])}</span><span>{fmt.format(new Date(`${s.entryDate}T00:00:00`))}</span></span>
                    <span className={`line-clamp-2 ${styles.cardTitle}`}>{sheetTitle(s)}</span>
                    <span className={`line-clamp-2 ${styles.snippet}`}>
                      {snippet(s)}
                    </span>
                    <span className={styles.cardFooter}>
                      {s.status !== "open" && (
                        <span
                          data-testid={`sheet-status-${s.status}`}
                          className={styles.status}
                        >
                          {t(`sheet.status.${s.status}`)}
                        </span>
                      )}
                      <span className={styles.openLink}>{t("sheet.listOpen")}<ArrowRight size={16} strokeWidth={1.5} aria-hidden="true" /></span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <PrimaryBar>
        <Button
          variant="primary"
          onClick={() => navigate("/lembar/baru")}
          data-testid="new-sheet-button"
          className="w-full md:w-auto md:min-w-[200px]"
        >
          <Plus size={18} strokeWidth={1.5} aria-hidden="true" />
          {t("sheet.new")}
        </Button>
      </PrimaryBar>
    </div>
  );
}
